import { versionDate } from '../api/versionLabel';
import { originalPdfForVersion } from '../api/originalPdfs';
import { OriginalPdfLink } from '../components/common/OriginalPdfLink';
import { useParams, useNavigate } from 'react-router';
import { useTimeline } from '../api/hooks';
import { QueryState } from '../components/common/QueryState';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Tooltip } from '../components/ui/Tooltip';
import { BookOpen, FileSearch, GitCompareArrows, History } from 'lucide-react';
import './work.timeline.css';

export function Component() {
  const { workId = '' } = useParams<{ workId: string }>();
  const timelineQuery = useTimeline(workId);
  const navigate = useNavigate();

  return (
    <div className="sedlex-timeline-view">
      <div className="sedlex-route-heading">
        <div className="sedlex-route-icon">
          <History aria-hidden="true" />
        </div>
        <div>
          <h2>Version history</h2>
          <p>
            Research versions begin with the principal Act and follow the supported
            amendments. Source certification dates do not establish legal commencement.
          </p>
        </div>
      </div>

      <QueryState query={timelineQuery}>
        {(events) => {
          const list = Array.isArray(events) ? [...events].reverse() : [];
          if (list.length === 0) {
            return (
              <Card style={{ padding: 24, textAlign: 'center' }}>
                <p style={{ color: 'var(--text-secondary)' }}>
                  No timeline versions found for this statutory work.
                </p>
              </Card>
            );
          }

          return (
            <div className="sedlex-timeline-container">
              <div className="sedlex-timeline-rail" />

              {list.map((ev, idx) => {
                const priorVersion = list[idx + 1] ?? null;
                const isLatest = idx === 0;
                return (
                  <div key={ev.version_id} className="sedlex-timeline-item">
                    <div
                      className={`sedlex-timeline-node ${ev.is_root ? 'sedlex-timeline-node--root' : ''}`}
                    />

                    <Card className="sedlex-timeline-card">
                      <div className="sedlex-timeline-header">
                        <div className="sedlex-timeline-title-row">
                          <Badge variant={ev.is_root ? 'accent' : 'neutral'}>
                            {ev.is_root
                              ? 'Principal Act'
                              : `Version ${ev.sequence_number}`}
                          </Badge>
                          <h3 className="sedlex-timeline-title">
                            {ev.amending_title ||
                              (ev.is_root
                                ? 'Principal Act as enacted'
                                : `Version ${ev.sequence_number}`)}
                          </h3>
                          {isLatest && <Badge variant="success">Latest available</Badge>}
                          {Boolean(ev.source_notes?.length) && (
                            <Badge variant="warning">
                              {ev.source_notes?.length} source-text notes
                            </Badge>
                          )}
                        </div>

                        <div className="sedlex-timeline-date">
                          <strong>{versionDate(ev)}</strong>
                        </div>
                      </div>

                      <div className="sedlex-timeline-meta-grid">
                        <div>
                          <span style={{ color: 'var(--text-secondary)' }}>
                            Applied changes:{' '}
                          </span>
                          <strong style={{ color: 'var(--success)' }}>
                            {ev.changes_applied}
                          </strong>
                        </div>

                        {ev.changes_unapplied > 0 ? (
                          <Tooltip content="Changes categorized as UNRESOLVED or AMBIGUOUS held for human review">
                            <div>
                              <span style={{ color: 'var(--text-secondary)' }}>
                                Held for review:{' '}
                              </span>
                              <strong style={{ color: 'var(--warning)' }}>
                                {ev.changes_unapplied}
                              </strong>
                            </div>
                          </Tooltip>
                        ) : (
                          <div>
                            <span style={{ color: 'var(--text-secondary)' }}>
                              Held for review:{' '}
                            </span>
                            <span>0</span>
                          </div>
                        )}

                        <div>
                          <span style={{ color: 'var(--text-secondary)' }}>
                            Provisions changed:{' '}
                          </span>
                          <strong>{ev.provisions_changed}</strong>
                        </div>
                      </div>

                      <div className="sedlex-timeline-actions">
                        <OriginalPdfLink pdf={originalPdfForVersion(ev.version_id)} />
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            void navigate(
                              `/work/${workId}/provisions?versionId=${ev.version_id}`,
                            );
                          }}
                        >
                          <BookOpen aria-hidden="true" /> Read this version
                        </Button>

                        {!ev.is_root && priorVersion && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              void navigate(`/work/${workId}/diff?to=${ev.version_id}`);
                            }}
                          >
                            <GitCompareArrows aria-hidden="true" /> Compare with v
                            {priorVersion.sequence_number}
                          </Button>
                        )}

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            void navigate(
                              `/work/${workId}/blame?versionId=${ev.version_id}`,
                            );
                          }}
                        >
                          <FileSearch aria-hidden="true" /> View sources
                        </Button>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          );
        }}
      </QueryState>
    </div>
  );
}
Component.displayName = 'TimelineView';
